import { useEffect, useState } from "react";
import type { Prospect } from "@/lib/types";
import { getVariants } from "@/lib/types";
import { EmailStatusTimeline } from "@/components/EmailStatusTimeline";
import { cn } from "@/lib/utils";

interface Props {
  prospect: Prospect;
  onApprove: (args: { variantIdx: number; subject: string; body: string }) => void;
  onReject: (note: string) => void;
  isSaving: boolean;
}

export function EmailReviewPanel({ prospect, onApprove, onReject, isSaving }: Props) {
  const variants = getVariants(prospect);
  const initialIdx = prospect.personalization_chosen_variant_idx ?? 0;
  const [idx, setIdx] = useState(initialIdx < variants.length ? initialIdx : 0);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  // Recharge les champs éditables au changement de variante OU de prospect.
  // Recompute les variantes depuis prospect pour rester correct si le parent
  // réutilise le composant monté avec un autre lead.
  useEffect(() => {
    const vs = getVariants(prospect);
    const safeIdx = idx < vs.length ? idx : 0;
    if (safeIdx !== idx) setIdx(safeIdx);
    const v = vs[safeIdx];
    setSubject(v ? v.subject : (prospect.personalization_subject ?? ""));
    setBody(v ? v.body : (prospect.personalization_body ?? ""));
  }, [idx, prospect]);

  const handleApprove = () => {
    if (
      !body.includes("{{unsubscribe}}") &&
      !window.confirm(
        "Le lien {{unsubscribe}} est absent du corps (obligatoire RGPD). Approuver quand même ?"
      )
    )
      return;
    onApprove({ variantIdx: idx, subject, body });
  };

  if (variants.length === 0) {
    return (
      <div className="aurora-tile-white p-5 text-sm text-neutral-500">
        Pas encore personnalisé — l'email sera généré par le workflow de qualification.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="aurora-tile-white p-4">
        <div className="text-[10px] uppercase tracking-wide text-neutral-400">
          Statut de l'email
        </div>
        <div className="mt-2">
          <EmailStatusTimeline status={prospect.status} />
        </div>
      </section>

      <section className="aurora-tile-white p-5">
        <div className="mb-3 flex gap-1.5">
          {variants.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium",
                i === idx ? "bg-aurora-navy text-white" : "bg-neutral-100 text-neutral-600"
              )}
            >
              Variante {i + 1}
            </button>
          ))}
        </div>

        <label className="text-[10px] uppercase tracking-wide text-neutral-400" htmlFor="subject">
          Sujet
        </label>
        <input
          id="subject"
          aria-label="Sujet"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="mb-1 mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-aurora-bright-blue focus:outline-none"
        />
        <div className="mb-3 text-right text-[10px] text-neutral-400">{subject.length} car.</div>

        <label className="text-[10px] uppercase tracking-wide text-neutral-400" htmlFor="body">
          Corps
        </label>
        <textarea
          id="body"
          aria-label="Corps"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          className="mb-1 mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm focus:border-aurora-bright-blue focus:outline-none"
        />
        <div className="mb-4 text-right text-[10px] text-neutral-400">
          {body.length} car. (cible 200-1500)
        </div>

        {!body.includes("{{unsubscribe}}") && (
          <p className="mb-3 text-xs font-medium text-red-600">
            ⚠ Le lien de désinscription {"{{unsubscribe}}"} est absent du corps (obligatoire RGPD).
          </p>
        )}

        <div className="flex items-center gap-2">
          <button
            disabled={isSaving}
            onClick={handleApprove}
            className="rounded-md bg-aurora-green px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {isSaving ? "Enregistrement…" : "Approuver"}
          </button>
          <button
            disabled={isSaving}
            onClick={() => onReject(window.prompt("Note de rejet (optionnel) :") ?? "")}
            className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 disabled:opacity-50"
          >
            Rejeter
          </button>
        </div>
      </section>
    </div>
  );
}
